import React from "react";
import {
  Box,
  Typography,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

const CircleMeasurements = ({ measurementData, volumesData }) => {
  const circleConfig = {
    CSF: { color: "#ff0000", name: "Central Subfield (CSF)" }, // Red
    ISF: { color: "#ffff00", name: "Inner Subfield (ISF)" }, // Yellow
    OSF: { color: "#00ff00", name: "Outer Subfield (OSF)" }, // Green
  };

  const formatValue = (value) => {
    return Number(value).toFixed(2);
  };

  const formatIntValue = (value) => {
    return Math.round(Number(value));
  };

  const renderMeasurementRow = (
    key,
    label,
    value,
    unit,
    isPreformatted = false
  ) => (
    <Box
      key={key}
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        fontSize: "11px",
        paddingLeft: "24px",
        paddingRight: "8px",
        minHeight: "20px",
      }}
    >
      <Typography
        variant="caption"
        sx={{ fontSize: "11px", fontWeight: "medium" }}
      >
        {label}:
      </Typography>
      <Typography variant="caption" sx={{ fontSize: "11px" }}>
        {isPreformatted ? value : formatValue(value)} {unit}
      </Typography>
    </Box>
  );

  // Don't render if measurementData is null or undefined
  if (!measurementData) {
    return null;
  }

  return (
    <Box sx={{ marginTop: "8px", marginBottom: "45px" }}>
      <Divider sx={{ margin: "8px 0" }} />
      <Typography
        variant="subtitle2"
        sx={{
          fontSize: "12px",
          fontWeight: "bold",
          paddingLeft: "16px",
          marginBottom: "4px",
          color: "rgba(0,0,0,0.7)",
        }}
      >
        Rings measurements
      </Typography>

      {/* Subheading for thickness & volume section */}

      {Object.entries(measurementData)?.map(([circleType, data]) => {
        const config = circleConfig[circleType];
        if (!config) return null;
        const regionVolumes = volumesData?.regions?.[circleType];

        return (
          <Box key={circleType} sx={{ marginBottom: "4px" }}>
            <ListItem
              sx={{
                py: 0,
                minHeight: 28,
                backgroundColor: "rgba(255, 255, 255)",
                color: "rgba(0,0,0)",
              }}
            >
              <ListItemIcon sx={{ minWidth: "22px" }}>
                <RadioButtonUncheckedIcon
                  sx={{
                    color: config.color,
                    fontSize: "14px",
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary={config.name}
                primaryTypographyProps={{
                  fontSize: 12,
                  fontWeight: "bold",
                }}
              />
            </ListItem>
            <Typography
              variant="caption"
              sx={{
                fontSize: "11px",
                fontWeight: "bold",
                paddingLeft: "16px",
                marginTop: "2px",
                color: "rgba(0,0,0,0.7)",
              }}
            >
              Thickness & Volume:
            </Typography>

            {/* Measurement details */}
            {renderMeasurementRow(
              `${circleType}-volume`,
              "Volume",
              data.volume_mm3,
              "mm³"
            )}
            {renderMeasurementRow(
              `${circleType}-mean`,
              "Mean",
              formatIntValue(data.mean_µm),
              "µm",
              true
            )}
            {renderMeasurementRow(
              `${circleType}-max`,
              "Max",
              formatIntValue(data.max_µm),
              "µm",
              true
            )}
            {renderMeasurementRow(
              `${circleType}-min`,
              "Min",
              formatIntValue(data.min_µm),
              "µm",
              true
            )}

            {regionVolumes ? (
              <>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "11px",
                    fontWeight: "bold",
                    paddingLeft: "16px",
                    marginTop: "6px",
                    color: "rgba(0,0,0,0.7)",
                  }}
                >
                  Fluid Volume:
                </Typography>
                {renderMeasurementRow(
                  `${circleType}-fluid-irf`,
                  "IRF",
                  regionVolumes.IRF?.volume_mm3 ?? 0,
                  "mm³"
                )}
                {renderMeasurementRow(
                  `${circleType}-fluid-srf`,
                  "SRF",
                  regionVolumes.SRF?.volume_mm3 ?? 0,
                  "mm³"
                )}
                {renderMeasurementRow(
                  `${circleType}-fluid-ped`,
                  "PED",
                  regionVolumes.PED?.volume_mm3 ?? 0,
                  "mm³"
                )}
              </>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
};

export default CircleMeasurements;
